import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PermissionCheckerService } from 'abp-ng2-module';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ConversationsCloseReason, ConversationsOpenRequest } from './conversations-overlay.models';

/** The permission every Global Conversations surface needs (C# `AppPermissions.Pages_Agenda`; SD2-24, PERM-NEW). */
export const CONVERSATIONS_PERMISSION = 'Pages.Agenda';

/** Where `open()` goes when no overlay host is mounted (e.g. `/app/settings`, outside MainLayout; SD2-23, §6.5). */
export const CONVERSATIONS_HOST_URL = '/app/main/my-day';

/**
 * `ConversationsOverlayService` — the typed open/close channel of the one Global Conversations overlay
 * (GLOBAL_CONVERSATIONS_SLICE2_SPEC.md §6.1). Triggers (the sidebar item, the Projects list chip, the project tab, a
 * card breadcrumb) call `open()`; the overlay component that MainLayout mounts once is the HOST: it connects with
 * `connectHost()`, listens to `requests$` / `closeRequests$`, and reports its state through `reportOpen()`, which
 * drives the sidebar item's active state (`isOpen$`).
 *
 * Rules:
 *  - `open()` is a no-op without `Pages.Agenda` (checked on every call: permissions load after bootstrap).
 *  - With no host connected (`/app/settings` is outside MainLayout) the request is kept as PENDING and the app
 *    navigates once to My Day; MainLayout mounts the overlay, whose `connectHost()` takes the pending request exactly
 *    once. A second `open()` while that navigation runs replaces the pending request without navigating again.
 *  - `close()` only emits; it is safe with no host and while closed (MainLayout calls `close('destination')` before
 *    every task, project and client panel it opens).
 *  - `isOpen$` changes only through `reportOpen()`; `disconnectHost()` does not touch it (the host reports
 *    `reportOpen(false)` itself when it is destroyed while open).
 *  - It keeps no All-scope memory (owner 2026-10-21, "clear search and filters on every fresh open"; amends SD2-20):
 *    nothing of an open survives its close, so the memory is per open, in the overlay's own state
 *    (`OverlayState.allMemory`, `gc/state/overlay-state.ts`).
 *
 * Deliberately a small root service, the `EventFormService` pattern, never a `DataService` event-bus channel
 * (STANDARDS: don't grow the DataService event-bus).
 */
@Injectable({ providedIn: 'root' })
export class ConversationsOverlayService {
    private readonly _requests = new Subject<ConversationsOpenRequest>();
    private readonly _closeRequests = new Subject<ConversationsCloseReason>();
    private readonly _isOpen = new BehaviorSubject<boolean>(false);
    private readonly _taskOverPanel = new BehaviorSubject<boolean>(false);
    private _taskOverPanelMarked = false;
    private _pending: ConversationsOpenRequest | null = null;
    private _hostConnected = false;
    private _navigating = false;

    /** Open requests for the connected host (never replayed: a request made with no host waits in the pending slot). */
    readonly requests$: Observable<ConversationsOpenRequest> = this._requests.asObservable();

    /** Close requests for the connected host. */
    readonly closeRequests$: Observable<ConversationsCloseReason> = this._closeRequests.asObservable();

    /** Whether the overlay is open, as its host reports it; drives the sidebar item's active state. Starts false. */
    readonly isOpen$: Observable<boolean> = this._isOpen.asObservable();

    /**
     * True while a task opened from the overlay's task name is shown over the open overlay (owner 2026-10-19, tester
     * findings): the host drops the panel just under the task panel until MainLayout reports the task panel closed.
     */
    readonly taskOverPanel$: Observable<boolean> = this._taskOverPanel.asObservable();

    constructor(private readonly _router: Router, private readonly _permissionChecker: PermissionCheckerService) {}

    /**
     * Open the overlay (or switch an open overlay to the requested scope). A no-op without `Pages.Agenda`. With a host
     * connected it emits on `requests$`; without one it keeps the request as pending and navigates to My Day once.
     */
    open(request: ConversationsOpenRequest): void {
        if (!request || !this._permissionChecker.isGranted(CONVERSATIONS_PERMISSION)) {
            return;
        }
        if (this._hostConnected) {
            this._requests.next(request);
            return;
        }
        this._pending = request;
        if (this._navigating) {
            return;
        }
        this._navigating = true;
        this._router.navigateByUrl(CONVERSATIONS_HOST_URL).then(
            (navigated: boolean) => this.onNavigationSettled(navigated),
            () => this.onNavigationSettled(false)
        );
    }

    /**
     * Ask the host to close the overlay. Only emits: safe with no host connected and while already closed. A closed
     * overlay has nothing stacked over it, so a task shown over it no longer counts (a later open is on top again, N-6).
     */
    close(reason: ConversationsCloseReason): void {
        this.endTaskOverPanel();
        this._closeRequests.next(reason);
    }

    /**
     * The overlay's task name (the breadcrumb's task, the project-scope "Task: ‹task›" button) opens the task OVER the
     * overlay, which stays open (owner 2026-10-19, tester findings): marks the next task-panel open, read once by
     * `takeTaskOverPanel()`.
     */
    markTaskOverPanel(): void {
        this._taskOverPanelMarked = true;
    }

    /**
     * MainLayout, on every task-panel open: true when that open was marked by `markTaskOverPanel()` (keep the overlay
     * open; the task shows over it), false otherwise (close the overlay first, as every task open does, SD2-01).
     * Clears the mark.
     */
    takeTaskOverPanel(): boolean {
        const overPanel: boolean = this._taskOverPanelMarked;

        this._taskOverPanelMarked = false;
        if (overPanel && !this._taskOverPanel.value) {
            this._taskOverPanel.next(true);
        }
        return overPanel;
    }

    /** The task panel closed (MainLayout), or the overlay closed: nothing is stacked over the overlay any more. */
    endTaskOverPanel(): void {
        this._taskOverPanelMarked = false;
        if (this._taskOverPanel.value) {
            this._taskOverPanel.next(false);
        }
    }

    /**
     * The host (the overlay component) calls this once from `ngOnInit`. Returns the pending request, if any, and
     * clears it, so it is applied exactly once; null when nothing is pending.
     */
    connectHost(): ConversationsOpenRequest | null {
        const pending = this._pending;

        this._hostConnected = true;
        this._navigating = false;
        this._pending = null;
        return pending;
    }

    /** The host calls this from `ngOnDestroy`. Leaves `isOpen$` untouched. */
    disconnectHost(): void {
        this._hostConnected = false;
        this._navigating = false;
    }

    /** The host reports whether its overlay is open. The only way `isOpen$` changes; emits only on a change. */
    reportOpen(open: boolean): void {
        const next = !!open;

        if (this._isOpen.value !== next) {
            this._isOpen.next(next);
        }
    }

    /**
     * The navigation to My Day ended. A refused or failed navigation (a guard said no, another navigation replaced
     * it) drops the pending request, so it can never surprise the user on a later visit; a successful one leaves it
     * for `connectHost()` (which may already have taken it).
     */
    private onNavigationSettled(navigated: boolean): void {
        this._navigating = false;
        if (!navigated && !this._hostConnected) {
            this._pending = null;
        }
    }
}
